// src/oura/onboarding/drafts.ts
//
// The first draft of every onboarding email. Staff read it, change what they
// want, and send it from Oura. Short on purpose: one ask per email.

import type { KickoffDraft, MessageDraft, MessageKind, Onboarding, OnboardingPerson, SentMessage } from "@service/aeo/onboarding";
import { ACCESS_LABEL, accessAsked, accessOwner, accessRecipient, callName, greetName, isHeld, isOneSetup, lastSent, type NextAction } from "./derive";
import { GBP_REQUEST_LINE, SITE_GUIDES } from "./guides";

const SIGN_OFF = "Angel";

/**
 * What the form link already carries, said so the client knows what to check.
 *
 * The WEBSITE, and nothing else. The form link prefills one field, and the
 * backend says so where it builds it: "The outlet areas are not prefilled on
 * the form: Oura reads them itself and staff check them before the first
 * scan." The email used to promise the areas too whenever Oura happened to
 * know them, which sent a client looking for a field that is not there
 * (#3989).
 */
const FILLED_IN = "Your website is already filled in.";

/**
 * The kick-off's recipients and nothing else. The email is a SendGrid
 * template (`first_name`, `onboarding_url`), so there is no subject or body to
 * draft; staff change who it goes to and who is copied (#4155).
 */
export function draftKickoff(input: { contact: OnboardingPerson }): KickoffDraft {
    return { to: [input.contact], cc: [] };
}

function draftFormReminder(o: Onboarding): MessageDraft {
    if (isOneSetup(o)) return draftSetupReminder(o);
    return {
        to: [o.contact],
        cc: [],
        subject: "Your 11 questions, when you have 10 minutes",
        body: [
            `Hi ${greetName(o.contact.name)},`,
            // An Online client's first check waits for brand checks, not the form: the form brings the login.
            o.buyMode === "online"
                ? `Your Oura login follows once these are answered. ${FILLED_IN}`
                : `Your first AI check starts once these are answered. ${FILLED_IN}`,
            o.form.link,
            SIGN_OFF,
        ].join("\n\n"),
    };
}

/** One setup: the questions and the access are on one page, so one reminder names what is left of both. */
function draftSetupReminder(o: Onboarding): MessageDraft {
    const open = accessAsked(o).filter((a) => !isHeld(a) && a.status !== "claimed");
    const left = [...(o.form.answeredAt ? [] : ["the questions about your business"]), ...open.map((a) => ACCESS_LABEL[a.key])];
    return {
        to: [o.contact],
        cc: [],
        subject: "Your Oura setup, when you have 10 minutes",
        body: [
            `Hi ${greetName(o.contact.name)},`,
            `Still to do: ${left.join(", ")}.`,
            "It is all on your Setup page in Oura and saves as you go. If someone else looks after a step, you can send it to them from there. No passwords needed.",
            SIGN_OFF,
        ].join("\n\n"),
    };
}

function accessRecipients(o: Onboarding, to: OnboardingPerson = accessRecipient(o)): Pick<MessageDraft, "to" | "cc"> {
    const isContact = to.email.toLowerCase() === o.contact.email.toLowerCase();
    return { to: [to], cc: isContact ? [] : [o.contact] };
}

/**
 * The access request's recipients and nothing else. The email is a SendGrid
 * template (variable `first_name`, ourteam-backend#5813), so there is no subject
 * or body to draft; staff change who it goes to and who is copied (#4261). To
 * whoever the form named for access, with the main contact copied.
 */
export function draftAccessRequest(o: Onboarding): KickoffDraft {
    return accessRecipients(o);
}

function draftAccessReminder(o: Onboarding): MessageDraft {
    // To whoever still has something to do, which changes as access comes in.
    const { to, cc } = accessRecipients(o, accessOwner(o));
    const open = accessAsked(o).filter((a) => !isHeld(a) && a.status !== "claimed");
    const left = open.map((a) => ACCESS_LABEL[a.key]);
    // The access contact has no Oura login, so their steps are written out.
    // The login holder is pointed at the page that has them. Whether the
    // recipient holds the login is read from who they are, not from whether
    // anyone is copied: once only Search Console and Analytics are left the
    // login holder is the recipient, and the main contact may still be copied.
    const loginHolder = o.login.contact ?? o.contact;
    const hasLogin = to[0].email.toLowerCase() === loginHolder.email.toLowerCase();
    const invites = [
        open.some((a) => a.key === "gbp") ? GBP_REQUEST_LINE : null,
        open.some((a) => a.key === "website") ? SITE_GUIDES[o.siteBuilder ?? "other"].requestLine : null,
    ].filter((line): line is string => !!line);
    const google = open.some((a) => a.key === "gsc" || a.key === "ga4");
    const steps = hasLogin
        ? ["The steps for each one are on your Set-up page in Oura. No passwords needed."]
        : [
            ...invites.map((line, i) => `${i + 1}. ${line}`),
            ...(google ? [`${callName(loginHolder.name)} connects Search Console and Analytics inside Oura.`] : []),
            "No passwords needed.",
        ];
    return {
        to,
        cc,
        subject: `${left.length} left to set up`,
        body: [`Hi ${greetName(to[0].name)},`, `Still to do: ${left.join(", ")}.`, ...steps, SIGN_OFF].join("\n\n"),
    };
}

/**
 * Oura's draft for a message staff can edit. The kick-off and the access request
 * are templates with only recipients to choose (`draftKickoff`, `draftAccessRequest`).
 */
export function draftFor(kind: Exclude<MessageKind, "kickoff" | "access_request">, o: Onboarding): MessageDraft {
    switch (kind) {
        case "form_reminder": return draftFormReminder(o);
        case "access_reminder": return draftAccessReminder(o);
    }
}

/**
 * Who the client's login is created for, and what to say about the address.
 *
 * THE CONTACT, always. `form.answers.contactEmail` is whoever FILLED THE FORM
 * IN -- the backend takes `respondent_email` first -- so on a form answered
 * from a personal or a shared Google account it is not the client's address at
 * all. It used to be the default in the release drawer, while the read-only
 * preview beside it went on showing the contact, so the field and the preview
 * disagreed and "Send both" would have created the login for the wrong person
 * (#4049).
 *
 * The form address is not thrown away. When it differs, `note` names it, so
 * staff switch to it deliberately rather than by not noticing. `warn` is true
 * only when there is something to notice, so the ordinary case stays quiet.
 */
export interface LoginRecipient {
    name: string;
    email: string;
    note: string;
    warn: boolean;
}

export function loginRecipient(o: Onboarding): LoginRecipient {
    const answered = o.form.answers;
    const formEmail = answered?.contactEmail?.trim() || null;
    const contactEmail = o.contact.email?.trim() || null;
    const name = o.contact.name?.trim() || answered?.contactName?.trim() || "";
    if (!contactEmail && formEmail) {
        // Nothing better to offer, so the form's address stands -- named, not
        // slipped in. An empty field would only make staff go and find it.
        return {
            name,
            email: formEmail,
            note: "This client has no contact email, so this is the address the form was filled in from. Check it before sending.",
            warn: true,
        };
    }
    if (formEmail && contactEmail && formEmail.toLowerCase() !== contactEmail.toLowerCase()) {
        return {
            name,
            email: contactEmail,
            note: `The form was filled in from ${formEmail}. The login goes to the contact unless you change it.`,
            warn: true,
        };
    }
    return { name, email: contactEmail ?? "", note: "From the client's contact.", warn: false };
}

export const MESSAGE_LABEL: Record<MessageKind | "login_invite" | "account_ready", string> = {
    kickoff: "Kick-off email",
    form_reminder: "Form reminder",
    access_request: "Access request",
    access_reminder: "Access reminder",
    login_invite: "Login invite",
    account_ready: "Account ready email",
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isEmail = (s: string): boolean => EMAIL.test(s.trim());

/** "Sarah Lim <sarah@x.com>, daniel@y.com" style lists are typed by hand, so both forms are read. */
export function parsePeople(text: string): { people: OnboardingPerson[]; bad: string[] } {
    const people: OnboardingPerson[] = [];
    const bad: string[] = [];
    for (const part of text.split(/[,;\n]/).map((p) => p.trim()).filter(Boolean)) {
        const m = part.match(/^(.*?)<([^>]+)>$/);
        const email = (m ? m[2] : part).trim();
        const name = (m ? m[1] : "").trim().replace(/^"|"$/g, "");
        if (isEmail(email)) people.push({ name: name || email.split("@")[0], email });
        else bad.push(part);
    }
    return { people, bad };
}

export function formatPeople(people: OnboardingPerson[]): string {
    return people.map((p) => (p.name && p.name !== p.email.split("@")[0] ? `${p.name} <${p.email}>` : p.email)).join(", ");
}

/**
 * What goes out when an access request is sent again: the same people. The email
 * is a SendGrid template now (ourteam-backend#5813), so the words are not ours to
 * repeat: the backend sends the template whatever subject or body a message once
 * carried, including an access request sent before the template. Null when it
 * cannot be: another kind of message, or a message with no recipient.
 *
 * `POST /onboarding/messages` with kind `access_request` records it as a new row,
 * and nothing is read from the contact as it stands now.
 */
export function resendDraftOf(message: SentMessage): KickoffDraft | null {
    if (message.kind !== "access_request" || message.to.length === 0) return null;
    return { to: message.to, cc: message.cc };
}

/**
 * Who the release goes to, as the drawer prefills it (F10, call 15): the
 * login from `loginRecipient()`, the access request to whoever looks after
 * access. Said before the drawer opens, so nobody presses blind.
 */
export function releaseLine(o: Onboarding, next: Pick<NextAction, "kind">): string {
    const login = loginRecipient(o);
    const access = accessRecipient(o);
    const loginOwed = o.login.status === "not_sent";
    const requestOwed = !lastSent(o, "access_request");
    const person = (p: { name: string; email: string }) => `${callName(p.name)}, ${p.email}`;
    const same = login.email.trim().toLowerCase() === access.email.trim().toLowerCase();
    if (loginOwed && requestOwed) {
        return same
            ? `Both go to ${person(login)}, as Angel.`
            : `The login goes to ${person(login)}. The access request goes to ${person(access)}. Both as Angel.`;
    }
    if (loginOwed) return `The login goes to ${person(login)}, as Angel.`;
    return next.kind === "release" ? `The access request goes to ${person(access)}, as Angel.` : "";
}
